"use client";
import { useEffect, useState, useMemo } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  increment,
} from "firebase/firestore";
import { db, auth, storage } from "../config/firebase";
import {
  ref,
  listAll,
  getDownloadURL,
  uploadBytes,
  deleteObject,
  getMetadata,
} from "firebase/storage";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { FreeMode, Navigation, Thumbs } from "swiper/modules";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import {
  useParams,
  useSearchParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Modal from "react-modal";
import { useGeoLocation } from "../components/useGeoLocation";
import { distanceBetween, geohashForLocation } from "geofire-common";
import SafeMedia from "../components/SafeMedia";
import UpgradeModal from "../components/UpgradeModal";
import { getTownAndCountry } from "../utils/geo";
import { geocode } from "../utils/geocoding";
import { useListenStatus } from "../hooks/useListenToRTDB";

export const formatKmPretty = (km) => {
  if (!Number.isFinite(km)) return null;
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${Math.round(km)} km`;
};

if (typeof window !== "undefined") {
  console.assert(formatKmPretty(0.4) === "400 m");
  console.assert(formatKmPretty(0.99) === "990 m");
  console.assert(formatKmPretty(1.1) === "1 km");
  console.assert(formatKmPretty(2.49) === "2 km");
  console.assert(formatKmPretty(2.5) === "3 km");
}

export const Profile = () => {
  const location = useLocation();
  const isEdit = location.pathname.includes("edit");

  const editMode = isEdit ?? false;

  const [user] = useAuthState(auth);
  const { locationInfo, locationError } = useGeoLocation();
  const [profileData, setProfileData] = useState(null);
  const [userDocId, setUserDocId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [userPhotos, setUserPhotos] = useState([]);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [newPhoto, setNewPhoto] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { uid: paramUid } = useParams();
  const uid =
    searchParams.get("uid") ?? location.state?.uid ?? paramUid ?? null;
  // const [editMode, setEditMode] = useState(false);
  const [iLoggedView, setILoggedView] = useState(false);

  const [myDocId, setMyDocId] = useState("");
  const isOwnProfile = !!(user && uid && user.uid === uid);
  const [myData, setMyData] = useState(null);
  const isBlocked = !!myData?.blocked?.includes(uid);
  const [theyBlockedMe, setTheyBlockedMe] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const [viewLimitOpen, setViewLimitOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [locationName, setLocationName] = useState(null);
  const maxPhotos = useMemo(
    () => (profileData?.isPremium ? 25 : 5),
    [profileData]
  );
  const maxVideos = useMemo(
    () => (profileData?.isPremium ? 5 : 0),
    [profileData]
  );
  const [likes, setLikes] = useState([]);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [manualLocationInput, setManualLocationInput] = useState("");
  const [onlineUsersList, setOnlineUsersList] = useState([]);

  const statuses = useListenStatus();

  useEffect(() => {
    setOnlineUsersList(statuses);
  }, [statuses]);

  const toNum = (v) => (typeof v === "number" ? v : parseFloat(v));
  const safeDistanceKm = (from, to) => {
    const lat1 = toNum(from?.latitude),
      lon1 = toNum(from?.longitude);
    const lat2 = toNum(to?.latitude),
      lon2 = toNum(to?.longitude);
    if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;
    return distanceBetween([lat1, lon1], [lat2, lon2]);
  };

  const [displayPhoto, setDisplayPhoto] = useState({
    url: null,
    fullPath: null,
  });

  const lookingForList = [
    "relationship",
    "right now",
    "friends",
    "casual",
    "Daddy",
    "leather",
    "kink",
    "fetish",
    "watersports",
    "roleplay",
    "discreet",
    "sauna",
    "public",
    "twink",
    "bear",
    "otter",
    "chub",
    "crossdresser",
    "dating",
    "holding hands",
    "body play",
    "cuddles",
    "kissing",
    "oral",
    "anal",
    "foreplay",
    "bottom",
    "top",
    "versatile",
    "side",
    "boyfriend",
    "trans",
    "chaser",
    "old",
    "young",
    "hairy",
    "smooth",
    "anon",
    "balaclava",
    "glory hole",
    "blindfold",
    "handcuffs",
    "BDSM",
    "conversation",
    "voice call",
    "video call",
    "messaging",
    "nudes",
    "furries",
  ];
  const locationPrefList = ["yours", "mine", "outside", "cafe", "bar", "other"];

  useEffect(() => {
    if (!uid || !user) {
      setProfileData(null);
      setLoading(false);
      return;
    }
    const fetchUserProfile = async () => {
      setLoading(true);
      try {
        const userQuery = query(
          collection(db, "users"),
          where("authUID", "==", uid)
        );
        const querySnapshot = await getDocs(userQuery);
        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          setUserDocId(userDoc.id);
          const data = userDoc.data();
          setProfileData(data);
          const blocked = Array.isArray(data.blocked) ? data.blocked : [];
          if (user) setTheyBlockedMe(blocked.includes(user.uid));
          setDisplayPhoto({
            url: data.displayPhotoUrl || null,
            fullPath: data.displayPhotoPath || null,
          });
        } else {
          setProfileData(null);
        }
      } catch (err) {
        console.error("fetchUserProfile error:", err);
        setProfileData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchUserProfile();
  }, [uid, user, refreshTrigger]);

  useEffect(() => {
    if (!uid) return;
    const fetchPhotos = async () => {
      try {
        const folderRef = ref(storage, `userMedia/${uid}`);
        const res = await listAll(folderRef);
        const photos = await Promise.all(
          res.items.map(async (item) => {
            const url = await getDownloadURL(item);
            const metadata = await getMetadata(item);
            const isVideo = metadata.contentType?.startsWith("video/") ?? false;
            return { url, fullPath: item.fullPath, isVideo };
          })
        );
        setUserPhotos(photos);
      } catch (e) {
        console.error(e);
      }
    };
    fetchPhotos();
  }, [uid, refreshTrigger]);

  const turnOnEditMode = () => {
    if (!isOwnProfile) return;
    navigate(`/profile/edit/${uid}`);
    // setEditMode(true);
  };

  useEffect(() => {
    if (!user) {
      setMyDocId("");
      setMyData(null);
      return;
    }
    const fetchMe = async () => {
      try {
        const meQ = query(
          collection(db, "users"),
          where("authUID", "==", user.uid)
        );
        const meSnap = await getDocs(meQ);
        if (!meSnap.empty) {
          const meDoc = meSnap.docs[0];
          setMyDocId(meDoc.id);
          setMyData(meDoc.data());
        }
      } catch (e) {
        console.error("fetch current user error:", e);
      }
    };
    fetchMe();
  }, [user, refreshTrigger]);

  useEffect(() => {
    if (profileData?.location) {
      const { latitude, longitude } = profileData.location;
      getTownAndCountry(latitude, longitude).then(setLocationName);
    }
  }, [profileData?.location]);

  const handleRefreshLocation = async () => {
    if (!isOwnProfile || !locationInfo) {
      alert(
        "Could not get your location. Please ensure you have enabled location services in your browser."
      );
      return;
    }
    const { latitude, longitude } = locationInfo;
    await saveProfileData({ location: { latitude, longitude } });
    setRefreshTrigger((t) => t + 1);
  };

  useEffect(() => {
    if (isOwnProfile && locationInfo && myDocId) {
      const { latitude, longitude } = locationInfo;
      const userRef = doc(db, "users", myDocId);
      updateDoc(userRef, { location: { latitude, longitude } });
    }
  }, [isOwnProfile, locationInfo, myDocId]);

  useEffect(() => {
    if (locationError) {
      alert(`Error getting your location: ${locationError}`);
    }
  }, [locationError]);

  const saveProfileData = async (data) => {
    if (!isOwnProfile) {
      alert("You can only edit your own profile.");
      return false;
    }
    setSaving(true);
    try {
      const userRef = doc(db, "users", userDocId);
      await updateDoc(userRef, {
        ...data,
        ...(displayPhoto?.url
          ? {
            displayPhoto: displayPhoto.url,
            displayPhotoUrl: displayPhoto.url,
            displayPhotoPath: displayPhoto.fullPath,
          }
          : {}),
      });
      setProfileData((prev) => ({ ...prev, ...data }));
      return true;
    } catch (err) {
      console.error("saveProfileData error:", err);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const uploadProfilePhoto = async () => {
    if (!newPhoto || !isOwnProfile) {
      return;
    }

    const isVideo = newPhoto.type.startsWith("video/");
    if (isVideo) {
      if (!myData?.isPremium) {
        setUpgradeModalOpen(true);
        setNewPhoto(null);
        return;
      }
      const videoCount = userPhotos.filter((p) => p.isVideo).length;
      if (videoCount >= maxVideos) {
        setLimitOpen(true);
        setNewPhoto(null);
        return;
      }
    }

    if (userPhotos.length >= maxPhotos) {
      if (profileData?.isPremium) setLimitOpen(true);
      else setUpgradeOpen(true);
      setNewPhoto(null);
      return;
    }

    try {
      const mediaRef = ref(
        storage,
        `userMedia/${user.uid}/${user.uid + newPhoto.name}`
      );
      const snap = await uploadBytes(mediaRef, newPhoto);
      const url = await getDownloadURL(snap.ref);
      const added = { url, fullPath: snap.ref.fullPath, isVideo };
      setUserPhotos((prev) => [...prev, added]);

      if (!displayPhoto?.url) {
        const userRef = doc(db, "users", userDocId);
        await updateDoc(userRef, {
          displayPhoto: url,
          displayPhotoUrl: url,
          displayPhotoPath: snap.ref.fullPath,
        });
        setDisplayPhoto({ url, fullPath: snap.ref.fullPath });
      }
    } catch (err) {
      console.error("uploadProfilePhoto error:", err);
      setSaveNotice({
        type: "error",
        msg: "There was an error uploading your media. Please try again.",
      });
      setTimeout(() => setSaveNotice(null), 3000);
    } finally {
      setNewPhoto(null);
    }
  };

  useEffect(() => {
    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    const logProfileView = async () => {
      try {
        if (iLoggedView) return;
        if (!user || !uid) return;
        if (uid === user.uid) return;
        if (myData?.isStealth) return;
        if (isBlocked || theyBlockedMe) return;

        const viewRef = doc(db, "profileViews", `${uid}_${user.uid}`);
        const existing = await getDoc(viewRef);
        let alreadyCountedToday = false;
        if (existing.exists()) {
          const ts = existing.data()?.viewedAt;
          const dt = ts?.toDate ? ts.toDate() : null;
          if (dt && sameDay(dt, new Date())) {
            alreadyCountedToday = true;
          }
        }

        if (myData?.isPremium) {
          await setDoc(
            viewRef,
            {
              targetUid: uid,
              reportedUID:uid,
              viewerUid: user.uid,
              viewedAt: serverTimestamp(),
            },
            { merge: true }
          );
          setILoggedView(true);
          return;
        }

        if (!alreadyCountedToday) {
          const todayStr = new Date().toISOString().slice(0, 10);
          const counterRef = doc(
            db,
            "dailyViewCounters",
            `${user.uid}_${todayStr}`
          );
          const counterSnap = await getDoc(counterRef);
          const currentCount = counterSnap.exists()
            ? counterSnap.data()?.count || 0
            : 0;

          if (currentCount >= 20) {
            return;
          }

          await setDoc(
            counterRef,
            { date: todayStr, count: increment(1) },
            { merge: true }
          );
        }

        await setDoc(
          viewRef,
          {
            targetUid: uid,
            reportedUID:uid,
            viewerUid: user.uid,
            viewedAt: serverTimestamp(),
          },
          { merge: true }
        );
        setILoggedView(true);
      } catch (e) {
        console.error("logProfileView error:", e);
      }
    };
    logProfileView();
  }, [
    profileData,
    myData?.isPremium,
    myData?.isStealth,
    isBlocked,
    theyBlockedMe,
    uid,
    user,
    iLoggedView,
  ]);

  const handleMessageUser = async () => {
    if (isBlocked) {
      alert(
        "You have blocked this user. Unblock them to start a conversation."
      );
      return;
    }
    if (theyBlockedMe) {
      alert("This user is not available to message.");
      return;
    }
    const me = user.uid;
    const them = uid;
    const mid = [me, them].sort().join("_");
    const msgsRef = doc(db, "messages", mid);
    const chatSnap = await getDoc(msgsRef);
    if (!chatSnap.exists()) {
      await setDoc(msgsRef, {
        participants: [me, them],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastText: "",
      });
    }
    navigate(`/message/${mid}`);
  };

  const handleBlockUser = async () => {
    if (!user || !myDocId) return;
    if (uid === user.uid) {
      alert("You can't block yourself.");
      return;
    }
    try {
      const meRef = doc(db, "users", myDocId);
      if (isBlocked) {
        await updateDoc(meRef, { blocked: arrayRemove(uid) });
      } else {
        setModalOpen(true);
        await updateDoc(meRef, { blocked: arrayUnion(uid) });
      }
      setRefreshTrigger((t) => t + 1);
    } catch (e) {
      console.error("handleBlockUser error:", e);
      alert("Sorry—something went wrong updating your block list.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isOwnProfile) {
      alert("You can only edit your own profile.");
      return;
    }

    // Immediately save text-based data
    const fd = new FormData(e.target);
    const payload = Object.fromEntries(fd);
    const selectedLocationPref = fd.getAll("locationPref");
    const selectedLookingFor = fd.getAll("lookingFor");
    payload.locationPref =
      selectedLocationPref.length > 0
        ? selectedLocationPref
        : profileData?.locationPref ?? [];
    payload.lookingFor =
      selectedLookingFor.length > 0
        ? selectedLookingFor
        : profileData?.lookingFor ?? [];
    if ("location" in payload) delete payload.location;

    const ok = await saveProfileData(payload);

    if (ok) {
      setSaveNotice({
        type: "success",
        msg: "Your profile changes have been saved.",
      });
      navigate(`/profile/${uid}`);
      // setEditMode(false);
      // Trigger photo upload in the background
      if (newPhoto) {
        uploadProfilePhoto();
      }
    } else {
      setSaveNotice({
        type: "error",
        msg: "Sorry, we couldn’t save your changes. Please try again.",
      });
    }

    setTimeout(() => setSaveNotice(null), 3000);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleDelete = async (index) => {
    if (!isOwnProfile) return;
    const photo = userPhotos[index];
    const isDeletingDisplay =
      displayPhoto?.fullPath && photo.fullPath === displayPhoto.fullPath;
    setUserPhotos((ps) => ps.filter((_, i) => i !== index));
    try {
      await deleteObject(ref(storage, photo.fullPath));
      if (isDeletingDisplay) {
        const userRef = doc(db, "users", userDocId);
        await updateDoc(userRef, {
          displayPhoto: null,
          displayPhotoUrl: null,
          displayPhotoPath: null,
        });
        setDisplayPhoto({ url: null, fullPath: null });
      }
    } catch (err) {
      console.error("deleteObject failed:", err);
      setRefreshTrigger((t) => t + 1);
    }
  };

  const handleSetDisplay = async (photo) => {
    if (!isOwnProfile || !editMode) return;
    if (photo.isVideo) {
      alert("You can't set a video as your display photo.");
      return;
    }
    try {
      const userRef = doc(db, "users", userDocId);
      await updateDoc(userRef, {
        displayPhoto: photo.url,
        displayPhotoUrl: photo.url,
        displayPhotoPath: photo.fullPath,
      });
      setDisplayPhoto({ url: photo.url, fullPath: photo.fullPath });
      setSaveNotice({ type: "success", msg: "Display photo updated." });
      setTimeout(() => setSaveNotice(null), 2500);
    } catch (e) {
      console.error("set display photo failed:", e);
      alert("Could not set display photo.");
    }
  };

  const orderedPhotos = useMemo(() => {
    if (!displayPhoto?.fullPath) return userPhotos;
    const [head, ...rest] = userPhotos.reduce((acc, p) => {
      if (p.fullPath === displayPhoto.fullPath) acc.unshift(p);
      else acc.push(p);
      return acc;
    }, []);
    return head ? [head, ...rest] : userPhotos;
  }, [userPhotos, displayPhoto]);

  useEffect(() => {
    const idx = orderedPhotos.findIndex(
      (p) => p.fullPath === displayPhoto?.fullPath
    );
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [orderedPhotos, displayPhoto]);

  const renderLocation = () => {
    if (isOwnProfile) {
      if (locationName) {
        return (
          <span>
            {locationName.town}, {locationName.country}
          </span>
        );
      }
      return <span>Your location</span>;
    }
    if (locationName) {
      return (
        <span>
          {locationName.town}, {locationName.country}
        </span>
      );
    }
    let from = profileData?.location;
    if (typeof from === "string") {
      const [latStr, lonStr] = from.split(",").map((s) => s.trim());
      const lat = parseFloat(latStr),
        lon = parseFloat(lonStr);
      if (Number.isFinite(lat) && Number.isFinite(lon))
        from = { latitude: lat, longitude: lon };
    }
    const km = from && locationInfo ? safeDistanceKm(from, locationInfo) : null;
    if (km == null) return <em>Location unavailable</em>;
    const pretty = formatKmPretty(km);
    return <span>{pretty} away</span>;
  };

  const handleManualLocationSave = async () => {
    if (!manualLocationInput) return;
    const result = await geocode(manualLocationInput);
    if (result) {
      const { latitude, longitude } = result;
      const geohash = geohashForLocation([latitude, longitude]);
      await saveProfileData({ location: { latitude, longitude, geohash } });
      setRefreshTrigger((t) => t + 1);
      setShowLocationModal(false);
      setManualLocationInput("");
    } else {
      alert("Could not find that location. Please try again.");
    }
  };

  const handleViewLikes = () => {
    navigate("/discover", { state: { viewMode: "my-likes" } });
  };

  const online = onlineUsersList?.[profileData?.authUID]?.isOnline ?? false;

  if (loading && profileData === null) return <p>Loading profile…</p>;

  return (
    <div className="main profile">
      <Modal
        className="upgrade-prompt"
        isOpen={showLocationModal}
        onRequestClose={() => setShowLocationModal(false)}
        contentLabel="Enter Location"
      >
        <h2>Enter Your Location</h2>
        <input
          type="text"
          value={manualLocationInput}
          onChange={(e) => setManualLocationInput(e.target.value)}
          placeholder="e.g., London, UK"
        />
        <div className="actions">
          <button onClick={() => setShowLocationModal(false)}>Cancel</button>
          <button onClick={handleManualLocationSave}>Save</button>
        </div>
      </Modal>
      <Modal
        className="likes-modal"
        isOpen={showLikesModal}
        onRequestClose={() => setShowLikesModal(false)}
        contentLabel="Users who liked you"
      >
        <h2>Who Liked Me</h2>
        <div className="likes-list">
          {likes.length > 0 ? (
            likes.map((like) => (
              <div
                key={like.id}
                className="like-item"
                onClick={() => navigate(`/profile/${like.authUID}`)}
              >
                <img
                  src={like.displayPhotoUrl || "/img/placeholder.jpg"}
                  alt={like.displayName}
                />
                <p>{like.displayName}</p>
              </div>
            ))
          ) : (
            <p>No one has liked your profile yet.</p>
          )}
        </div>
        <button onClick={() => setShowLikesModal(false)}>Close</button>
      </Modal>

      <div className="block-modal">
        <Modal
          className="block-success"
          isOpen={modalOpen}
          onRequestClose={() => setModalOpen(false)}
          contentLabel="block info"
        >
          <h2>
            This user is now blocked and will no longer be able to see you in
            discovery mode or message you.
          </h2>
          <p>
            You can unblock this user by clicking unblock below or through the
            blocked users page in settings.
          </p>
          <p>
            You can report this user in your account settngs under "Blocked
            Users".
          </p>
          <button onClick={() => setModalOpen(false)}>Close</button>
        </Modal>
      </div>

      <div className="view-limit-modal">
        <Modal
          className="upgrade-prompt"
          isOpen={viewLimitOpen}
          onRequestClose={() => setViewLimitOpen(false)}
          contentLabel="Upgrade to view more profiles"
        >
          <h2>Daily limit reached</h2>
          <p>
            You’ve viewed 20 profiles today. Upgrade to Premium to keep browsing
            without limits.
          </p>
          <div className="actions">
            <button onClick={() => setViewLimitOpen(false)}>Maybe later</button>
            <button
              onClick={() => {
                setViewLimitOpen(false);
                navigate("/settings/upgrade");
              }}
            >
              Upgrade
            </button>
          </div>
        </Modal>
      </div>

      <UpgradeModal
        isOpen={upgradeModalOpen}
        onRequestClose={() => setUpgradeModalOpen(false)}
        title="Viewing and Adding Videos is Premium"
        message="Upgrade to Premium to enable this feature."
      />

      <div className="upgrade-modal">
        <Modal
          className="upgrade-prompt"
          isOpen={upgradeOpen}
          onRequestClose={() => setUpgradeOpen(false)}
          contentLabel="Upgrade to Premium"
        >
          <h2>Get more photo slots with Premium</h2>
          <p>
            Free: up to 5 photos. Premium: up to 15 photos. You've reached your
            current limit.
          </p>
          <div className="actions">
            <button onClick={() => setUpgradeOpen(false)}>Maybe later</button>
            <button
              onClick={() => {
                setUpgradeOpen(false);
                navigate("/pricing");
              }}
            >
              Upgrade
            </button>
          </div>
        </Modal>
      </div>

      <div className="limit-modal">
        <Modal
          className="limit-prompt"
          isOpen={limitOpen}
          onRequestClose={() => setLimitOpen(false)}
          contentLabel="Photo Limit Reached"
        >
          <h2>Photo limit reached</h2>
          <p>
            Premium accounts can have up to 15 photos. Remove one to add
            another.
          </p>
          <div className="actions">
            <button onClick={() => setLimitOpen(false)}>Okay</button>
          </div>
        </Modal>
      </div>

      <div className="content">
        {saveNotice && (
          <div
            role="status"
            aria-live="polite"
            className={`notice ${saveNotice.type === "success" ? "notice-success" : "notice-error"
              }`}
            style={{
              marginBottom: "12px",
              padding: "10px 12px",
              borderRadius: "8px",
              background:
                saveNotice.type === "success"
                  ? "rgba(16,185,129,0.15)"
                  : "rgba(239,68,68,0.15)",
              border: `1px solid ${saveNotice.type === "success"
                ? "rgba(16,185,129,0.6)"
                : "rgba(239,68,68,0.6)"
                }`,
            }}
          >
            {saveNotice.msg}
          </div>
        )}
        <div className="profile-actions">
          <div className="back-button">
            <button type="button" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
          {isOwnProfile && (
            <div className="edit-user">
              <button onClick={turnOnEditMode}>Edit</button>
            </div>
          )}
          {!isOwnProfile && (
            <div className="dropdown">
              <button onClick={handleBlockUser}>
                {isBlocked ? "Unblock" : "Block/Report"}
              </button>
            </div>
          )}
          {!isOwnProfile && (
            <div className="message-user">
              <button onClick={handleMessageUser}>Message</button>
            </div>
          )}
        </div>

        {profileData ? (
          <form onSubmit={handleSubmit} className="profile-form">
            <div className="profile-photos relative">
              <Swiper
                spaceBetween={10}
                slidesPerView={1}
                centeredSlides
                navigation
                thumbs={{ swiper: thumbsSwiper }}
                modules={[FreeMode, Navigation, Thumbs]}
                className="mySwiper2"
                onSlideChange={(sw) => setActiveIndex(sw.activeIndex)}
                style={{
                  "--swiper-navigation-color": "#fff",
                  "--swiper-pagination-color": "#fff",
                }}
              >
                {/* {orderedPhotos.map((media, i) => (
                  <SwiperSlide key={media.fullPath}>
                    <div className="relative">
                      {media.isVideo ? (
                        !isOwnProfile && !myData?.isPremium ? (
                          <div className="relative">
                            <div
                              style={{
                                paddingTop: "56.25%",
                                backgroundColor: "#000",
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                              <button
                                onClick={() => setUpgradeModalOpen(true)}
                                className="bg-blue-600 text-white rounded px-4 py-2"
                              >
                                Upgrade to View Video
                              </button>
                            </div>
                          </div>
                        ) : (
                          <video
                            src={media.url}
                            controls
                            className="block w-full h-auto"
                          />
                        )
                      ) : (
                        <SafeMedia
                          src={media.url}
                          height={200}
                          alt={`Photo ${i + 1}`}
                          className="block w-full h-auto"
                        />
                      )}
                      {displayPhoto?.fullPath === media.fullPath && (
                        <span className="absolute left-2 top-2 z-10 bg-black/70 text-white text-xs px-2 py-1 rounded">
                          Display photo
                        </span>
                      )}
                      {isOwnProfile && editMode && (
                        <div className="absolute right-2 top-2 z-10 flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleSetDisplay(media)}
                            className={`bg-[#FBEAD3] cursor-pointer rounded px-3 py-1.5 text-sm font-medium transition `}
                            disabled={displayPhoto?.fullPath === media.fullPath}
                          >
                            {displayPhoto?.fullPath === media.fullPath
                              ? "Current display"
                              : "Make display photo"}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(i)}
                            className="deletebutton"
                          >
                            &times;
                          </button>
                        </div>
                      )}
                    </div>
                  </SwiperSlide>
                ))} */}

                {orderedPhotos.map((media, i) => (
                  <SwiperSlide key={media.fullPath}>
                    <div className="media-slide">
                      {media.isVideo ? (
                        !isOwnProfile && !myData?.isPremium ? (
                          <div className="video-locked">
                            <div className="video-locked-placeholder" />
                            <div className="video-locked-overlay">
                              <button
                                onClick={() => setUpgradeModalOpen(true)}
                                className="upgrade-video-btn"
                              >
                                Upgrade to View Video
                              </button>
                            </div>
                          </div>
                        ) : (
                          <video
                            src={media.url}
                            controls
                            className="media-video"
                          />
                        )
                      ) : (
                        <SafeMedia
                          src={media.url}
                          height={200}
                          alt={`Photo ${i + 1}`}
                          className="media-image"
                        />
                      )}

                      {displayPhoto?.fullPath === media.fullPath && (
                        <span className="display-photo-label">
                          Display photo
                        </span>
                      )}

                      {isOwnProfile && editMode && (
                        <div className="photo-action-buttons">
                          <button
                            type="button"
                            onClick={() => handleSetDisplay(media)}
                            className="set-display-btn"
                            disabled={displayPhoto?.fullPath === media.fullPath}
                          >
                            {displayPhoto?.fullPath === media.fullPath
                              ? "Current display"
                              : "Make display photo"}
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(i)}
                            className="deletebutton"
                          >
                            &times;
                          </button>
                        </div>
                      )}
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <Swiper
                onSwiper={setThumbsSwiper}
                spaceBetween={10}
                slidesPerView={4}
                freeMode
                watchSlidesProgress
                modules={[FreeMode, Navigation, Thumbs]}
                className="mySwiper"
              >
                {orderedPhotos.map((media) => (
                  <SwiperSlide key={media.fullPath}>
                    <div
                      className={`relative border-2 ${displayPhoto?.fullPath === media.fullPath
                        ? "border-blue-600"
                        : "border-transparent"
                        }`}
                    >
                      {media.isVideo ? (
                        <div className="relative">
                          <video
                            src={media.url}
                            className="w-full h-auto"
                            preload="metadata"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                            <svg
                              className="w-8 h-8 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                            </svg>
                          </div>
                        </div>
                      ) : (
                        <SafeMedia src={media.url} alt="thumbnail" />
                      )}
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {isOwnProfile && (
                <div className="media-upload-section">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0];
                      if (!file) return;

                      const isVideo = file.type.startsWith("video/");
                      if (isVideo && !myData?.isPremium) {
                        setUpgradeModalOpen(true);
                        e.target.value = "";
                        return;
                      }

                      if (userPhotos.length >= maxPhotos) {
                        if (profileData?.isPremium) {
                          setLimitOpen(true);
                        } else {
                          setUpgradeOpen(true);
                        }
                        e.target.value = "";
                        return;
                      }
                      setNewPhoto(file);
                    }}
                    className="media-upload-input"
                  />

                  {newPhoto && (
                    <p className="media-upload-info">
                      Selected: {newPhoto.name}. Save to upload.
                    </p>
                  )}

                  {/* {editMode && orderedPhotos[activeIndex] && (
                    <div className="set-display-container">
                      <button
                        type="button"
                        onClick={() =>
                          handleSetDisplay(orderedPhotos[activeIndex])
                        }
                        disabled={
                          displayPhoto?.fullPath ===
                          orderedPhotos[activeIndex].fullPath
                        }
                        className="set-current-display-btn"
                      >
                        {displayPhoto?.fullPath ===
                        orderedPhotos[activeIndex].fullPath
                          ? "Current display photo"
                          : "Set current as display photo"}
                      </button>
                    </div>
                  )} */}
                </div>
              )}
            </div>

            <div className="profile-fields">
              {/* <div className="profile-title-group profile-key">
                {(online ?? false) && (
                  <span
                    className="online-indicator is-online"
                    aria-label="Online now"
                    title="Online now"
                  />
                )}
                <div className="details-name">
                  {editMode ? (
                    <>
                      <label htmlFor="displayName">Display name</label>
                      <input
                        type="text"
                        disabled={!editMode}
                        defaultValue={profileData.displayName}
                        placeholder={profileData.displayName}
                        id="displayName"
                        name="displayName"
                      />
                    </>
                  ) : (
                    <h1>{profileData.displayName}</h1>
                  )}
                </div>
                <div className="details-age">
                  {editMode ? (
                    <>
                      <label htmlFor="age">Age</label>
                      <input
                        type="number"
                        disabled={!editMode}
                        defaultValue={profileData.age}
                        placeholder={profileData.age}
                        id="age"
                        name="age"
                      />
                    </>
                  ) : (
                    <h2>{profileData.age}</h2>
                  )}
                </div>
              </div> */}
              <div className="profile-header-section">
                {(online ?? false) && (
                  <div className="profile-online-status">
                    <span
                      className="online-dot"
                      aria-label="Online now"
                      title="Online now"
                    />
                    <span className="online-text">online</span>
                  </div>
                )}

                <div className="profile-name-field">
                  {editMode ? (
                    <>
                      <label htmlFor="displayName">Display name</label>
                      <input
                        type="text"
                        disabled={!editMode}
                        defaultValue={profileData.displayName}
                        placeholder={profileData.displayName}
                        id="displayName"
                        name="displayName"
                      />
                    </>
                  ) : (
                    <h1 className="profile-name">{profileData.displayName}</h1>
                  )}
                </div>

                <div className="profile-age-field">
                  {editMode ? (
                    <>
                      <label htmlFor="age">Age</label>
                      <input
                        type="number"
                        disabled={!editMode}
                        defaultValue={profileData.age}
                        placeholder={String(profileData.age)}
                        id="age"
                        name="age"
                      />
                    </>
                  ) : (
                    <h2 className="profile-age">{profileData.age}</h2>
                  )}
                </div>
              </div>

              {/* <div className="profile-location">
                <div className="field-value-location">{renderLocation()}</div>
                {editMode && (
                  <div>
                    <button
                      type="button"
                      onClick={handleRefreshLocation}
                      className="link-button"
                    >
                      Refresh My Location
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowLocationModal(true)}
                      className="link-button"
                      data-testid="enter-location-btn"
                    >
                      Enter my Location
                    </button>
                  </div>
                )}
              </div> */}

              <div className="profile-location-section">
                <div className="profile-location-display">
                  {renderLocation()}
                </div>

                {editMode && (
                  <div className="profile-location-buttons">
                    <button
                      type="button"
                      onClick={handleRefreshLocation}
                      className="profile-location-btn"
                    >
                      🔄 Refresh My Location
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowLocationModal(true)}
                      data-testid="enter-location-btn"
                      className="profile-location-btn"
                    >
                      📍 Enter My Location
                    </button>
                  </div>
                )}
              </div>

              <div className="profile-group">
                <div className="details-pronouns">
                  {editMode ? (
                    <>
                      <label htmlFor="pronouns">Pronouns</label>
                      <select
                        name="pronouns"
                        id="pronouns"
                        defaultValue={profileData.pronouns}
                      >
                        <option value="she/her">She/Her</option>
                        <option value="he/him">He/Him</option>
                        <option value="they/them">They/Them</option>
                        <option value="other">Other</option>
                      </select>
                    </>
                  ) : (
                    <span className="pronoun-user-view">
                      {profileData.pronouns.charAt(0).toUpperCase() +
                        profileData.pronouns.slice(1)}
                    </span>
                  )}
                </div>
                <div className="details-sexuality">
                  {editMode ? (
                    <>
                      <label htmlFor="sexuality">Sexuality</label>
                      <select
                        name="sexuality"
                        id="sexuality"
                        disabled={!editMode}
                        defaultValue={profileData.sexuality}
                      >
                        <option value="gay">gay</option>
                        <option value="bisexual">bisexual</option>
                        <option value="pansexual">pansexual</option>
                        <option value="other">Other</option>
                      </select>
                    </>
                  ) : (
                    <span className="pronoun-user-view">
                      {profileData.sexuality.charAt(0).toUpperCase() +
                        profileData.sexuality.slice(1)}
                    </span>
                  )}
                </div>
                <div className="details-role">
                  {editMode ? (
                    <>
                      <label htmlFor="role">Role</label>
                      <select
                        name="role"
                        id="role"
                        disabled={!editMode}
                        defaultValue={profileData.role}
                      >
                        <option value="top">Top</option>
                        <option value="bottom">Bottom</option>
                        <option value="vers">Vers</option>
                        <option value="side">Side</option>
                        <option value="other">Other</option>
                      </select>
                    </>
                  ) : (
                    <span className="pronoun-user-view">
                      {profileData.role.charAt(0).toUpperCase() +
                        profileData.role.slice(1)}
                    </span>
                  )}
                </div>
              </div>

              <div className="details-meeting">
                <fieldset>
                  <span className="bold">Meet at</span>
                  <br />
                  <br />
                  {editMode ? (
                    locationPrefList.map((tag) => (
                      <label key={tag}>
                        <input
                          type="checkbox"
                          name="locationPref"
                          value={tag}
                          defaultChecked={profileData.locationPref.includes(
                            tag
                          )}
                        />
                        {tag}
                      </label>
                    ))
                  ) : (
                    <div className="field-value-location">
                      {profileData.locationPref.length > 0 ? (
                        profileData.locationPref.map((tag) => (
                          <span key={tag} className="badge">
                            {tag}
                          </span>
                        ))
                      ) : (
                        <em>None specified</em>
                      )}
                    </div>
                  )}
                </fieldset>
              </div>

              <div className="details-interests">
                <fieldset>
                  <span className="bold">Looking for</span>
                  <br />
                  <br />
                  {editMode ? (
                    lookingForList.map((tag) => (
                      <label key={tag}>
                        <input
                          type="checkbox"
                          name="lookingFor"
                          value={tag}
                          defaultChecked={profileData.lookingFor.includes(tag)}
                        />
                        {tag}
                      </label>
                    ))
                  ) : (
                    <div className="field-value-interests">
                      {profileData.lookingFor.length > 0 ? (
                        profileData.lookingFor.map((tag) => (
                          <span key={tag} className="badge">
                            {tag}
                          </span>
                        ))
                      ) : (
                        <em>None specified</em>
                      )}
                    </div>
                  )}
                </fieldset>
              </div>

              <div className="details-bio">
                <span className="bold">Bio</span>
                <br />
                <br />
                {editMode ? (
                  <textarea
                    defaultValue={profileData?.bio ?? ""}
                    placeholder={profileData?.bio ?? ""}
                    maxLength="500"
                    id="bio"
                    name="bio"
                  />
                ) : (
                  <p>{profileData?.bio ?? <em>No bio yet</em>}</p>
                )}
              </div>

              {isOwnProfile && (
                <button type="submit" disabled={loading || saving}>
                  {saving ? "Saving…" : "Save"}
                </button>
              )}
            </div>
          </form>
        ) : (
          <p>No profile found.</p>
        )}
      </div>
    </div>
  );
};
